import { Fragment, useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, MapPin, Pencil, Plus, Trash2, Warehouse, X } from "lucide-react";
import { api, fmt, apiError } from "@/lib/api";

const ZONE_STYLES = {
  origin: "border-amber-500/50 text-amber-400",
  local: "border-emerald-500/40 text-emerald-400",
  nearby: "border-sky-500/40 text-sky-400",
  extended: "border-orange-500/40 text-orange-400",
  outer: "border-neutral-500/40 text-neutral-400",
};

const EMPTY_ZONE = { pin: "", zone: "local", label: "", free_delivery: false, delivery_charge: "", active: true };
const FREE_BY_DEFAULT = ["origin", "local", "nearby"];
const inputCls = "h-11 w-full rounded-xl border border-white/10 bg-black/40 px-4 text-sm text-white placeholder:text-neutral-600 focus:border-amber-500/60 focus:outline-none";

export default function AdminDelivery() {
  const [data, setData] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [settings, setSettings] = useState(null);
  const [settingsForm, setSettingsForm] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [editing, setEditing] = useState(null); // zone row or null for new
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_ZONE);
  const [zoneQuery, setZoneQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmId, setConfirmId] = useState(null);

  const load = useCallback(() => {
    Promise.all([api.get("/admin/delivery/zones"), api.get("/admin/delivery/warehouses"), api.get("/admin/delivery/settings")])
      .then(([z, w, s]) => {
        setData(z.data);
        setWarehouses(w.data);
        setSettings(s.data);
        setSettingsForm({
          zones: Object.fromEntries(Object.entries(s.data.zones).map(([k, v]) => [k, { min_days: v.min_days, max_days: v.max_days }])),
          express_enabled: s.data.express.enabled,
          express_premium: s.data.express.premium,
          express_min_days: s.data.express.min_days,
          express_max_days: s.data.express.max_days,
          express_zones: s.data.express.zones || [],
          free_threshold: s.data.free_threshold,
        });
      })
      .catch((e) => toast.error(apiError(e, "Couldn't load delivery zones")));
  }, []);
  useEffect(load, [load]);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put("/admin/delivery/settings", {
        ...settingsForm,
        zones: Object.fromEntries(Object.entries(settingsForm.zones).map(([k, v]) => [k, { min_days: parseInt(v.min_days) || 0, max_days: parseInt(v.max_days) || 0 }])),
        express_premium: parseFloat(settingsForm.express_premium) || 0,
        express_min_days: parseInt(settingsForm.express_min_days) || 0,
        express_max_days: parseInt(settingsForm.express_max_days) || 0,
        express_zones: settingsForm.express_zones || [],
        free_threshold: parseFloat(settingsForm.free_threshold) || 0,
      });
      toast.success("Delivery settings saved");
    } catch (err) {
      toast.error(apiError(err, "Couldn't save settings"));
    } finally {
      setSavingSettings(false);
    }
  };

  const setZoneEst = (zone, key, val) =>
    setSettingsForm((s) => ({ ...s, zones: { ...s.zones, [zone]: { ...s.zones[zone], [key]: val } } }));

  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setModalOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [modalOpen]);

  const openNew = () => { setEditing(null); setForm(EMPTY_ZONE); setModalOpen(true); };
  const openEdit = (z) => {
    setEditing(z);
    setForm({ pin: z.pin, zone: z.zone, label: z.label || "", free_delivery: z.free_delivery, delivery_charge: z.delivery_charge, active: z.active });
    setModalOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, delivery_charge: parseFloat(form.delivery_charge) || 0 };
    try {
      if (editing) await api.put(`/admin/delivery/zones/${editing.id}`, payload);
      else await api.post("/admin/delivery/zones", payload);
      toast.success(editing ? "Zone rule updated" : `PIN ${payload.pin} added`);
      setModalOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err, "Couldn't save zone rule"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (z) => {
    if (confirmId !== z.id) {
      setConfirmId(z.id);
      setTimeout(() => setConfirmId((c) => (c === z.id ? null : c)), 3000);
      return;
    }
    try {
      await api.delete(`/admin/delivery/zones/${z.id}`);
      toast.success(`PIN ${z.pin} rule removed`);
      setConfirmId(null);
      load();
    } catch (err) {
      toast.error(apiError(err, "Couldn't delete zone rule"));
    }
  };

  const iconBtn = "flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 text-neutral-400 transition-colors";

  return (
    <div data-testid="admin-delivery">
      {warehouses.filter((w) => w.is_origin).map((w) => (
        <div key={w.id} className="mb-5 flex items-center gap-3 rounded-2xl border border-amber-500/25 bg-amber-500/[0.05] px-5 py-4" data-testid="origin-warehouse-card">
          <Warehouse className="h-5 w-5 shrink-0 text-amber-500" />
          <p className="text-sm text-neutral-300">
            <span className="font-bold text-white">{w.name}</span> — origin PIN <span className="font-mono font-bold text-amber-400">{w.pin}</span>.
            Delivery charges are set per PIN zone below; add zones as PAQVERO expands its service area.
          </p>
        </div>
      ))}

      <div className="mb-5 flex items-center justify-between gap-3">
        <p className="text-xs text-neutral-500">{data ? `${data.zones.length} PIN rules configured` : "Loading…"}</p>
        <div className="flex items-center gap-3">
          <input
            data-testid="zones-search"
            value={zoneQuery}
            onChange={(e) => setZoneQuery(e.target.value)}
            placeholder="Search PIN or area…"
            className="h-11 w-56 rounded-xl border border-white/10 bg-black/40 px-4 text-sm text-white placeholder:text-neutral-600 focus:border-amber-500/60 focus:outline-none"
          />
          <button data-testid="add-zone-btn" onClick={openNew}
            className="flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-3 text-sm font-bold text-black transition-colors hover:bg-amber-400">
            <Plus className="h-4 w-4" /> Add PIN Zone
          </button>
        </div>
      </div>

      {settingsForm && (
        <section className="mb-6 rounded-2xl border border-white/[0.07] bg-[#FFFEFA] p-6" data-testid="delivery-settings">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-lg font-medium tracking-tight">Zone Estimates & Express</h3>
            <button data-testid="settings-save" onClick={saveSettings} disabled={savingSettings}
              className="flex items-center gap-2 rounded-xl bg-amber-500 px-4 py-2.5 text-xs font-bold text-black transition-colors hover:bg-amber-400 disabled:opacity-60">
              {savingSettings && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Save Settings
            </button>
          </div>
          <p className="mt-1 text-xs text-neutral-500">Business-day estimates per zone, shown on product pages, cart and checkout. Changes never alter historical orders.</p>

          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {Object.entries(settingsForm.zones).map(([zone, est]) => (
              <div key={zone} className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-4" data-testid={`settings-zone-${zone}`}>
                <p className={`text-[11px] font-bold uppercase tracking-wider ${ZONE_STYLES[zone]?.split(" ")[1] || "text-neutral-400"}`}>{zone}</p>
                <div className="mt-2.5 flex items-center gap-2">
                  <input type="number" min="0" max="60" value={est.min_days} data-testid={`settings-min-${zone}`}
                    onChange={(e) => setZoneEst(zone, "min_days", e.target.value)}
                    className="h-9 w-full rounded-lg border border-white/10 bg-black/40 px-2.5 text-center text-sm font-bold text-white focus:border-amber-500/60 focus:outline-none" />
                  <span className="text-neutral-600">–</span>
                  <input type="number" min="0" max="60" value={est.max_days} data-testid={`settings-max-${zone}`}
                    onChange={(e) => setZoneEst(zone, "max_days", e.target.value)}
                    className="h-9 w-full rounded-lg border border-white/10 bg-black/40 px-2.5 text-center text-sm font-bold text-white focus:border-amber-500/60 focus:outline-none" />
                </div>
                <p className="mt-1.5 text-center text-[10px] text-neutral-600">business days</p>
              </div>
            ))}
          </div>

          <div className="mt-4 grid gap-3 lg:grid-cols-2">
            <div className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-4" data-testid="settings-express">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-white">Express Delivery</p>
                <button type="button" role="switch" aria-checked={settingsForm.express_enabled} data-testid="settings-express-enabled"
                  onClick={() => setSettingsForm((s) => ({ ...s, express_enabled: !s.express_enabled }))}
                  className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${settingsForm.express_enabled ? "bg-amber-500" : "bg-white/10"}`}>
                  <span className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all ${settingsForm.express_enabled ? "left-6" : "left-1"}`} />
                </button>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2">
                <div>
                  <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-neutral-500">Extra charge ₹</label>
                  <input type="number" min="0" value={settingsForm.express_premium} data-testid="settings-express-premium"
                    onChange={(e) => setSettingsForm((s) => ({ ...s, express_premium: e.target.value }))}
                    className="h-9 w-full rounded-lg border border-white/10 bg-black/40 px-2.5 text-center text-sm font-bold text-white focus:border-amber-500/60 focus:outline-none" />
                </div>
                <div>
                  <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-neutral-500">Min days</label>
                  <input type="number" min="0" max="60" value={settingsForm.express_min_days} data-testid="settings-express-min"
                    onChange={(e) => setSettingsForm((s) => ({ ...s, express_min_days: e.target.value }))}
                    className="h-9 w-full rounded-lg border border-white/10 bg-black/40 px-2.5 text-center text-sm font-bold text-white focus:border-amber-500/60 focus:outline-none" />
                </div>
                <div>
                  <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-neutral-500">Max days</label>
                  <input type="number" min="0" max="60" value={settingsForm.express_max_days} data-testid="settings-express-max"
                    onChange={(e) => setSettingsForm((s) => ({ ...s, express_max_days: e.target.value }))}
                    className="h-9 w-full rounded-lg border border-white/10 bg-black/40 px-2.5 text-center text-sm font-bold text-white focus:border-amber-500/60 focus:outline-none" />
                </div>
              </div>
              <div className="mt-3 border-t border-white/[0.07] pt-3">
                <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Available in zones <span className="normal-case text-neutral-600">(only checked zones offer Express)</span></p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {["origin", "local", "nearby", "extended", "outer"].map((z) => {
                    const on = (settingsForm.express_zones || []).includes(z);
                    return (
                      <button
                        key={z}
                        type="button"
                        data-testid={`settings-express-zone-${z}`}
                        aria-pressed={on}
                        onClick={() => setSettingsForm((s) => ({
                          ...s,
                          express_zones: on ? (s.express_zones || []).filter((x) => x !== z) : [...(s.express_zones || []), z],
                        }))}
                        className={`rounded-lg border px-3 py-1.5 text-[11px] font-bold capitalize transition-colors ${
                          on ? "border-amber-500/60 bg-amber-500/10 text-amber-400" : "border-white/10 text-neutral-500 hover:text-neutral-300"
                        }`}
                      >
                        {z}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-2 text-[10px] text-neutral-600">Express price = the PIN's standard delivery charge + the extra charge above (e.g. ₹49 + ₹100 = ₹149).</p>
              </div>
            </div>
            <div className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-4">
              <p className="text-sm font-semibold text-white">Free Delivery Threshold</p>
              <p className="mt-0.5 text-[11px] text-neutral-500">Standard delivery is free above this subtotal (after discounts) in eligible zones.</p>
              <input type="number" min="0" value={settingsForm.free_threshold} data-testid="settings-threshold"
                onChange={(e) => setSettingsForm((s) => ({ ...s, free_threshold: e.target.value }))}
                className="mt-3 h-10 w-40 rounded-lg border border-white/10 bg-black/40 px-3 text-sm font-bold text-white focus:border-amber-500/60 focus:outline-none" />
            </div>
          </div>
        </section>
      )}

      {!data ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-amber-500" /></div>
      ) : data.zones.length === 0 ? (
        <div className="rounded-2xl border border-white/[0.07] bg-[#FFFEFA] py-16 text-center text-sm text-neutral-500" data-testid="zones-empty">
          No delivery zones yet — add your first PIN rule.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-white/[0.07] bg-[#FFFEFA]">
          <table className="w-full min-w-[860px] text-left text-sm" data-testid="zones-table">
            <thead>
              <tr className="border-b border-white/[0.07] text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-500">
                <th className="px-5 py-4">PIN Code</th>
                <th className="px-5 py-4">Area</th>
                <th className="px-5 py-4">Delivery Zone</th>
                <th className="px-5 py-4">Free Delivery ₹1,000+</th>
                <th className="px-5 py-4">Delivery Charge</th>
                <th className="px-5 py-4">Estimate</th>
                <th className="px-5 py-4">Status</th>
                <th className="px-5 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.zones.filter((z) => {
                const n = zoneQuery.trim().toLowerCase();
                return !n || z.pin.includes(n) || (z.label || "").toLowerCase().includes(n);
              }).map((z) => (
                <tr key={z.id} className={`border-b border-white/[0.04] ${z.active ? "" : "opacity-45"}`} data-testid={`zone-row-${z.pin}`}>
                  <td className="px-5 py-3.5 font-mono font-bold text-white">{z.pin}</td>
                  <td className="px-5 py-3.5 text-neutral-400">{z.label || "—"}</td>
                  <td className="px-5 py-3.5">
                    <span className={`rounded border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${ZONE_STYLES[z.zone] || ZONE_STYLES.outer}`} data-testid={`zone-type-${z.pin}`}>{z.zone}</span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className={`text-xs font-bold ${z.free_delivery ? "text-emerald-400" : "text-neutral-600"}`}>{z.free_delivery ? "Eligible" : "Not eligible"}</span>
                  </td>
                  <td className="px-5 py-3.5 text-neutral-300">{fmt(z.delivery_charge)}</td>
                  <td className="px-5 py-3.5 text-xs text-neutral-400">
                    {settings?.zones?.[z.zone] ? `${settings.zones[z.zone].min_days}–${settings.zones[z.zone].max_days} days` : "—"}
                  </td>
                  <td className="px-5 py-3.5">
                    <span className={`text-xs font-bold ${z.active ? "text-emerald-400" : "text-neutral-500"}`}>{z.active ? "Active" : "Inactive"}</span>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex justify-end gap-2">
                      <button data-testid={`zone-edit-${z.pin}`} onClick={() => openEdit(z)} className={`${iconBtn} hover:border-amber-500/50 hover:text-amber-400`} aria-label="Edit"><Pencil className="h-3.5 w-3.5" /></button>
                      <button data-testid={`zone-delete-${z.pin}`} onClick={() => remove(z)}
                        className={`${iconBtn} ${confirmId === z.id ? "border-red-500/70 bg-red-500/15 text-red-400" : "hover:border-red-400/50 hover:text-red-400"}`}
                        aria-label="Delete" title={confirmId === z.id ? "Click again to confirm" : "Delete rule"}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AnimatePresence>
        {modalOpen && (
          <Fragment key="zone-modal">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setModalOpen(false)} className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm" />
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 30 }}
              className="fixed inset-x-4 top-[8vh] z-[85] mx-auto max-w-md rounded-2xl border border-white/10 bg-[#F1EDE3] p-6" data-testid="zone-modal">
              <div className="mb-5 flex items-center justify-between">
                <h3 className="font-display flex items-center gap-2 text-lg font-semibold tracking-tight">
                  <MapPin className="h-5 w-5 text-amber-500" /> {editing ? `Edit PIN ${editing.pin}` : "Add PIN Zone"}
                </h3>
                <button onClick={() => setModalOpen(false)} className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 text-neutral-400 hover:text-white" aria-label="Close"><X className="h-4 w-4" /></button>
              </div>
              <form onSubmit={save} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">PIN code</label>
                    <input data-testid="zone-form-pin" required value={form.pin} onChange={(e) => setForm((f) => ({ ...f, pin: e.target.value.replace(/\D/g, "").slice(0, 6) }))} placeholder="400055" className={`${inputCls} font-mono`} />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Zone</label>
                    <select data-testid="zone-form-zone" value={form.zone} onChange={(e) => setForm((f) => ({ ...f, zone: e.target.value, free_delivery: FREE_BY_DEFAULT.includes(e.target.value) }))} className={inputCls}>
                      {(data?.zone_types || []).map((z) => <option key={z.id} value={z.id}>{z.label}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Area label (optional)</label>
                  <input data-testid="zone-form-label" value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} placeholder="e.g. Kandivali West" className={inputCls} />
                </div>
                <div>
                  <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Delivery charge ₹</label>
                  <input data-testid="zone-form-charge" required type="number" min="0" step="1" value={form.delivery_charge} onChange={(e) => setForm((f) => ({ ...f, delivery_charge: e.target.value }))} placeholder="79" className={inputCls} />
                </div>
                <label className="flex cursor-pointer items-center justify-between rounded-xl border border-white/[0.07] bg-white/[0.02] p-4">
                  <span className="text-sm font-semibold text-white">Free delivery on orders ₹1,000+</span>
                  <button type="button" role="switch" aria-checked={form.free_delivery} data-testid="zone-form-free"
                    onClick={() => setForm((f) => ({ ...f, free_delivery: !f.free_delivery }))}
                    className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${form.free_delivery ? "bg-emerald-500" : "bg-white/10"}`}>
                    <span className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all ${form.free_delivery ? "left-6" : "left-1"}`} />
                  </button>
                </label>
                <label className="flex cursor-pointer items-center justify-between rounded-xl border border-white/[0.07] bg-white/[0.02] p-4">
                  <span className="text-sm font-semibold text-white">Active (deliveries allowed)</span>
                  <button type="button" role="switch" aria-checked={form.active} data-testid="zone-form-active"
                    onClick={() => setForm((f) => ({ ...f, active: !f.active }))}
                    className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${form.active ? "bg-amber-500" : "bg-white/10"}`}>
                    <span className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all ${form.active ? "left-6" : "left-1"}`} />
                  </button>
                </label>
                <button data-testid="zone-form-save" type="submit" disabled={saving}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-amber-500 py-3.5 text-sm font-bold text-black transition-colors hover:bg-amber-400 disabled:opacity-60">
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  {editing ? "Save Changes" : "Add Zone"}
                </button>
              </form>
            </motion.div>
          </Fragment>
        )}
      </AnimatePresence>
    </div>
  );
}
